import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, Pencil, Archive, Loader2, CheckCircle2, Sparkles } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Badge } from 'components/ui/badge';
import { Switch } from 'components/ui/switch';
import { Alert, AlertDescription } from 'components/ui/alert';
import { ToggleGroup, ToggleGroupItem } from 'components/ui/toggle-group';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'components/ui/table';
import DataTable from 'components/ui/data-table';
import LoadingState from 'components/shared/LoadingState';
import {
  getAdminBlogPosts,
  deleteBlogPost,
  getAdminBlogCategories,
  createBlogCategory,
  updateBlogCategory,
} from '../../services/api';

const STATUS_BADGE_VARIANT = {
  draft: 'outline',
  pending_review: 'warning',
  published: 'success',
  archived: 'destructive',
};

const STATUS_LABELS = {
  all: 'All',
  draft: 'Draft',
  pending_review: 'Pending review',
  published: 'Published',
  archived: 'Archived',
};

// "all" is not a whitelist — the client-side filter below passes every post
// through, whatever its status (including creator-submitted pending_review).
const STATUS_FILTERS = ['all', 'draft', 'pending_review', 'published', 'archived'];

// The backend paginates /admin/getBlogPosts (default page_size 20, max 100).
// The list view, status pills and sorting are all client-side, so fetch every
// page up front. Post rows are metadata-only (no body content), so this stays
// cheap even at a few hundred posts.
const fetchAllBlogPosts = async () => {
  const pageSize = 100;
  const first = await getAdminBlogPosts({ page: 1, page_size: pageSize });
  const posts = [...(first.posts || [])];
  const totalPages = first.total_pages || 1;
  for (let page = 2; page <= Math.min(totalPages, 50); page += 1) {
    const res = await getAdminBlogPosts({ page, page_size: pageSize });
    posts.push(...(res.posts || []));
  }
  return posts;
};

// ─────────────────────────────────────────────────────────────────────────────
// Category Manager Component
// ─────────────────────────────────────────────────────────────────────────────

const CategoryManager = ({ categories, onUpdate, onCreate }) => {
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [saving, setSaving] = useState(false);

  const handleEdit = (cat) => {
    setEditingId(cat.id);
    setEditName(cat.name);
  };

  const handleSave = async (id) => {
    setSaving(true);
    await onUpdate({ id, name: editName });
    setEditingId(null);
    setSaving(false);
  };

  const handleCreate = async () => {
    if (!newCategory.trim()) return;
    setSaving(true);
    await onCreate({ name: newCategory.trim() });
    setNewCategory('');
    setSaving(false);
  };

  const handleToggleActive = async (cat) => {
    await onUpdate({ id: cat.id, is_active: !cat.is_active });
  };

  return (
    <div>
      <h2 className="mb-3 text-token-xl font-semibold">Manage Categories</h2>

      {/* Add New Category */}
      <div className="mb-4 flex items-center gap-2">
        <Input
          className="w-[260px]"
          placeholder="New category name"
          value={newCategory}
          onChange={(e) => setNewCategory(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
        />
        <Button
          size="sm"
          onClick={handleCreate}
          disabled={saving || !newCategory.trim()}
        >
          {saving ? <Loader2 className="animate-spin" /> : <Plus />}
          Add
        </Button>
      </div>

      {/* Category List */}
      <div className="overflow-auto rounded-token-large border border-borderGrey bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Slug</TableHead>
              <TableHead>Order</TableHead>
              <TableHead>Active</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {categories.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <p className="py-6 text-center text-token-md text-muted-foreground">
                    No categories yet.
                  </p>
                </TableCell>
              </TableRow>
            ) : (
              categories.map((cat) => (
                <TableRow key={cat.id}>
                  <TableCell>
                    {editingId === cat.id ? (
                      <Input
                        className="h-8 w-[220px]"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSave(cat.id)}
                        autoFocus
                      />
                    ) : (
                      cat.name
                    )}
                  </TableCell>
                  <TableCell>
                    <span className="text-muted-foreground">{cat.slug}</span>
                  </TableCell>
                  <TableCell>{cat.display_order}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={Boolean(cat.is_active)}
                        onCheckedChange={() => handleToggleActive(cat)}
                        aria-label={cat.is_active ? 'Deactivate category' : 'Activate category'}
                      />
                      <Badge variant={cat.is_active ? 'success' : 'outline'}>
                        {cat.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    {editingId === cat.id ? (
                      <div className="inline-flex gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8"
                          onClick={() => handleSave(cat.id)}
                          disabled={saving}
                        >
                          {saving && <Loader2 className="animate-spin" />}
                          Save
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8"
                          onClick={() => setEditingId(null)}
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(cat)}
                        title="Rename"
                      >
                        <Pencil />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Blog Editor Component
// ─────────────────────────────────────────────────────────────────────────────

export default function BlogEditor() {
  const navigate = useNavigate();
  const [view, setView] = useState('posts');
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');

  // Archive confirmation (replaces window.confirm)
  const [archiveTarget, setArchiveTarget] = useState(null); // post id | null
  const [archiving, setArchiving] = useState(false);

  // Fetch data on mount
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [postsRes, categoriesRes] = await Promise.all([
        fetchAllBlogPosts(),
        getAdminBlogCategories(),
      ]);

      setPosts(postsRes);
      setCategories(categoriesRes.categories || []);
    } catch (err) {
      setError(err.message || 'Failed to load blog data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreatePost = () => {
    navigate('/blog/new');
  };

  const handleEditPost = useCallback(
    (post) => {
      navigate(`/blog/edit/${post.id}`);
    },
    [navigate]
  );

  const handleConfirmArchive = async () => {
    if (!archiveTarget) return;
    setArchiving(true);
    try {
      await deleteBlogPost(archiveTarget);
      setPosts(await fetchAllBlogPosts());
      toast.success('Post archived');
    } catch (err) {
      toast.error(err.message || 'Failed to archive post');
    } finally {
      setArchiving(false);
      setArchiveTarget(null);
    }
  };

  const handleCreateCategory = async (data) => {
    try {
      await createBlogCategory(data);
      // Re-fetch on create: the server assigns id, slug and display_order.
      const res = await getAdminBlogCategories();
      setCategories(res.categories || []);
      toast.success('Category created');
    } catch (err) {
      toast.error(err.message || 'Failed to create category');
    }
  };

  const handleUpdateCategory = async (data) => {
    try {
      await updateBlogCategory(data);
      // Apply the mutation locally — no full re-fetch for a simple rename/toggle.
      setCategories((prev) =>
        prev.map((c) => (c.id === data.id ? { ...c, ...data } : c))
      );
      toast.success('Category updated');
    } catch (err) {
      toast.error(err.message || 'Failed to update category');
    }
  };

  const filteredPosts = useMemo(
    () =>
      statusFilter === 'all'
        ? posts
        : posts.filter((p) => p.status === statusFilter),
    [posts, statusFilter]
  );

  const statusCounts = useMemo(() => {
    const counts = { all: posts.length };
    posts.forEach((p) => {
      counts[p.status] = (counts[p.status] || 0) + 1;
    });
    return counts;
  }, [posts]);

  const postColumns = useMemo(
    () => [
      {
        accessorKey: 'title',
        header: 'Title',
        cell: ({ row }) => (
          <div className="min-w-0">
            <p className="truncate text-token-md font-medium">{row.original.title}</p>
            <p className="truncate text-muted-foreground">/{row.original.slug}</p>
          </div>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <div className="flex flex-wrap items-center gap-1">
            <Badge variant={STATUS_BADGE_VARIANT[row.original.status] || 'outline'}>
              {STATUS_LABELS[row.original.status] || row.original.status}
            </Badge>
            {row.original.is_premium && <Badge variant="info">Premium</Badge>}
            {row.original.source_video_id && (
              <Badge variant="outline" title={`Generated from video ${row.original.source_video_id}`}>
                From video{row.original.target_gw != null ? ` · GW${row.original.target_gw}` : ''}
              </Badge>
            )}
          </div>
        ),
      },
      {
        accessorKey: 'author_name',
        header: 'Author',
        // Creator submissions carry the creator's name; admin posts are "DraftFC"
        cell: ({ row }) => row.original.author_name || 'DraftFC',
      },
      {
        accessorKey: 'category_name',
        header: 'Category',
        cell: ({ row }) => row.original.category_name || '–',
      },
      {
        id: 'season_gw',
        header: 'Season/GW',
        enableSorting: false,
        cell: ({ row }) =>
          row.original.season && row.original.gameweek
            ? `${row.original.season} GW${row.original.gameweek}`
            : row.original.season || '–',
      },
      {
        accessorKey: 'published_at',
        header: 'Published',
        cell: ({ row }) =>
          row.original.published_at
            ? new Date(row.original.published_at).toLocaleDateString()
            : '–',
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        meta: { cellClassName: 'text-right whitespace-nowrap' },
        cell: ({ row }) => (
          <div className="inline-flex items-center gap-1">
            {row.original.status === 'pending_review' && (
              <Button
                size="sm"
                className="h-8"
                onClick={() => handleEditPost(row.original)}
              >
                <CheckCircle2 /> Review & publish
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleEditPost(row.original)}
              title="Edit"
            >
              <Pencil />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="text-destructive hover:text-destructive"
              onClick={() => setArchiveTarget(row.original.id)}
              title="Archive"
            >
              <Archive />
            </Button>
          </div>
        ),
      },
    ],
    [handleEditPost]
  );

  if (loading) {
    return <LoadingState message="Loading blog posts..." />;
  }

  if (error) {
    return (
      <Alert variant="destructive" className="m-2">
        <AlertDescription className="flex items-center justify-between gap-4">
          <span>{error}</span>
          <Button variant="outline" size="sm" onClick={fetchData}>
            Retry
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div>
      {/* View Toggle */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <ToggleGroup
          type="single"
          value={view}
          onValueChange={(v) => v && setView(v)}
        >
          <ToggleGroupItem value="posts">Posts ({posts.length})</ToggleGroupItem>
          <ToggleGroupItem value="categories">
            Categories ({categories.length})
          </ToggleGroupItem>
        </ToggleGroup>

        {view === 'posts' && (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => navigate('/blog/generate')}>
              <Sparkles /> New from YouTube video
            </Button>
            <Button size="sm" onClick={handleCreatePost}>
              <Plus /> New Post
            </Button>
          </div>
        )}
      </div>

      {/* Content */}
      {view === 'posts' ? (
        <div className="flex flex-col gap-3">
          {/* Status filter pills — client-side over the already-fetched list */}
          <ToggleGroup
            type="single"
            value={statusFilter}
            onValueChange={(v) => v && setStatusFilter(v)}
            className="self-start"
          >
            {STATUS_FILTERS.map((s) => (
              <ToggleGroupItem key={s} value={s} size="sm">
                {STATUS_LABELS[s]}
                {statusCounts[s] ? ` (${statusCounts[s]})` : s === 'all' ? ' (0)' : ''}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>

          <DataTable
            columns={postColumns}
            data={filteredPosts}
            emptyMessage={
              statusFilter === 'all'
                ? 'No posts yet. Create your first blog post!'
                : `No ${(STATUS_LABELS[statusFilter] || statusFilter).toLowerCase()} posts.`
            }
          />
        </div>
      ) : (
        <CategoryManager
          categories={categories}
          onUpdate={handleUpdateCategory}
          onCreate={handleCreateCategory}
        />
      )}

      {/* Archive confirmation (replaces window.confirm) */}
      <Dialog
        open={Boolean(archiveTarget)}
        onOpenChange={(o) => !o && setArchiveTarget(null)}
      >
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>Archive this post?</DialogTitle>
          </DialogHeader>
          <p className="text-token-md text-muted-foreground">
            The post will be archived, not permanently deleted. You can restore it
            by editing the post and setting its status back.
          </p>
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setArchiveTarget(null)}
              disabled={archiving}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={handleConfirmArchive}
              disabled={archiving}
            >
              {archiving && <Loader2 className="animate-spin" />}
              Archive
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
